import React, { useState } from 'react'

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'

// ── Sample client data with real UK deadlines ──────────────────────────────
const CLIENTS = [
  {
    id: 1,
    name: 'Adeyemi Catering Ltd',
    contact: 'Kunle Adeyemi',
    phone: '+44 7700 900123',
    type: 'Limited Company',
    filing: 'VAT Return Q1 2025',
    deadline: '2026-04-30',
    daysLeft: 16,
    urgency: 'red',
    amount: '£4,217.00 payable',
    lastContact: '14 days ago',
  },
  {
    id: 2,
    name: 'FW Catering Ltd',
    contact: 'Folake Williams',
    phone: '+44 7700 900456',
    type: 'Limited Company',
    filing: 'Confirmation Statement',
    deadline: '2026-04-23',
    daysLeft: 9,
    urgency: 'red',
    amount: '£34 filing fee',
    lastContact: '7 days ago',
  },
  {
    id: 3,
    name: 'Tunde Adesanya',
    contact: 'Tunde Adesanya',
    phone: '+44 7700 900789',
    type: 'Sole Trader',
    filing: 'VAT Return Q1 2025',
    deadline: '2026-05-07',
    daysLeft: 23,
    urgency: 'amber',
    amount: '£2,840.00 payable',
    lastContact: '21 days ago',
  },
  {
    id: 4,
    name: 'JO Tech Solutions Ltd',
    contact: 'James Okonkwo',
    phone: '+44 7700 900321',
    type: 'Limited Company',
    filing: 'RTI Payroll — April 2026',
    deadline: '2026-05-19',
    daysLeft: 35,
    urgency: 'amber',
    amount: '£3,180 HMRC payment',
    lastContact: '3 days ago',
  },
  {
    id: 5,
    name: 'Chioma Nwosu Properties',
    contact: 'Chioma Nwosu',
    phone: '+44 7700 900654',
    type: 'Landlord',
    filing: 'Self Assessment 2024/25',
    deadline: '2027-01-31',
    daysLeft: 298,
    urgency: 'green',
    amount: 'Rental income to declare',
    lastContact: '45 days ago',
  },
  {
    id: 6,
    name: 'Sarah Mensah Consulting',
    contact: 'Sarah Mensah',
    phone: '+44 7700 900987',
    type: 'Sole Trader',
    filing: 'Self Assessment 2024/25',
    deadline: '2027-01-31',
    daysLeft: 298,
    urgency: 'green',
    amount: '£1,240 estimated liability',
    lastContact: '60 days ago',
  },
]

const URGENCY_COLORS = {
  red:   { bg: '#FFF0F0', border: '#E24B4A', dot: '#E24B4A', label: 'URGENT', text: '#A32D2D' },
  amber: { bg: '#FFF8E6', border: '#C9973A', dot: '#C9973A', label: 'SOON',   text: '#7A4A10' },
  green: { bg: '#EAF3DE', border: '#3B6D11', dot: '#3B6D11', label: 'OK',     text: '#3B6D11' },
}

const MSG_TEMPLATES = {
  'VAT Return': (c) => `Hi ${c.contact.split(' ')[0]}, this is a reminder from Sunshoos Accountants.\n\nYour VAT Return for Q1 2025 is due by ${new Date(c.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}. ${c.amount}.\n\nPlease ensure your records are up to date and contact us if you need any assistance preparing the return.\n\nKind regards,\nSunshoos Accountants`,

  'Confirmation Statement': (c) => `Hi ${c.contact.split(' ')[0]}, hope you're well.\n\nThis is a reminder that the Confirmation Statement for ${c.name} is due by ${new Date(c.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.\n\nPlease confirm that all company details (directors, shareholders, registered address) are still correct so we can file on time. ${c.amount}.\n\nMany thanks,\nSunshoos Accountants`,

  'RTI Payroll': (c) => `Hi ${c.contact.split(' ')[0]}, payroll reminder from Sunshoos Accountants.\n\nApril 2026 payroll is due to be processed. The Full Payment Submission (FPS) must be filed with HMRC on or before payday.\n\nHMRC payment due: ${c.amount} — payable by 19 May 2026.\n\nPlease send us any payroll changes (new starters, leavers, salary changes) as soon as possible.\n\nKind regards,\nSunshoos Accountants`,

  'Self Assessment': (c) => `Hi ${c.contact.split(' ')[0]}, a gentle reminder from Sunshoos Accountants.\n\nAlthough the Self Assessment deadline is ${new Date(c.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}, we recommend starting early to avoid any surprises.\n\nPlease begin gathering your records: income, expenses, bank statements, and any rental or investment income for 2024/25.\n\nFeel free to call or message us if you have any questions.\n\nKind regards,\nSunshoos Accountants`,
}

function getTemplate(client) {
  const key = Object.keys(MSG_TEMPLATES).find(k => client.filing.includes(k))
  return key ? MSG_TEMPLATES[key](client) : `Hi ${client.contact.split(' ')[0]}, this is a reminder from Sunshoos Accountants regarding your upcoming ${client.filing} due by ${client.deadline}. Please get in touch if you need any assistance.\n\nKind regards,\nSunshoos Accountants`
}

export default function WhatsAppEngine() {
  const [selected, setSelected] = useState(null)
  const [messages, setMessages] = useState({})   // clientId -> message text
  const [statuses, setStatuses] = useState({})   // clientId -> 'draft'|'approved'|'sent'
  const [aiLoading, setAiLoading] = useState({}) // clientId -> bool
  const [filter, setFilter] = useState('all')    // all|red|amber|green
  const [tab, setTab] = useState('queue')        // queue|sent|settings

  const filtered = CLIENTS.filter(c => filter === 'all' ? true : c.urgency === filter)

  const urgentCount = CLIENTS.filter(c => c.urgency === 'red').length
  const amberCount  = CLIENTS.filter(c => c.urgency === 'amber').length

  // Generate message — either from template or GPT-4o
  async function generateMessage(client, useAI = false) {
    if (useAI) {
      setAiLoading(prev => ({ ...prev, [client.id]: true }))
      try {
        const res = await fetch(`${API}/api/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: `Draft a professional but warm WhatsApp message for a UK accounting practice (Sunshoos Accountants, East London, IFA-accredited) to send to their client.

Client name: ${client.contact}
Business: ${client.name} (${client.type})
Filing required: ${client.filing}
Deadline: ${new Date(client.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
Days remaining: ${client.daysLeft} days
Amount/action: ${client.amount}
Last contact: ${client.lastContact}

Requirements:
- Start with "Hi [first name],"
- Mention the specific filing and deadline clearly
- Include the amount or action required
- Keep it under 200 words
- Professional but friendly tone — like a trusted accountant
- End with "Kind regards, Sunshoos Accountants"
- No emojis
- Plain text only (WhatsApp format)`
          })
        })
        const data = await res.json()
        setMessages(prev => ({ ...prev, [client.id]: data.reply }))
        setStatuses(prev => ({ ...prev, [client.id]: 'draft' }))
      } catch {
        setMessages(prev => ({ ...prev, [client.id]: getTemplate(client) }))
        setStatuses(prev => ({ ...prev, [client.id]: 'draft' }))
      }
      setAiLoading(prev => ({ ...prev, [client.id]: false }))
    } else {
      setMessages(prev => ({ ...prev, [client.id]: getTemplate(client) }))
      setStatuses(prev => ({ ...prev, [client.id]: 'draft' }))
    }
    setSelected(client.id)
  }

  function approve(clientId) {
    setStatuses(prev => ({ ...prev, [clientId]: 'approved' }))
  }

  function sendMessage(client) {
    const msg = messages[client.id] || getTemplate(client)
    const encoded = encodeURIComponent(msg)
    const phone = client.phone.replace(/\s/g, '')
    window.open(`https://wa.me/${phone.replace('+','')}?text=${encoded}`, '_blank')
    setStatuses(prev => ({ ...prev, [client.id]: 'sent' }))
  }

  const sentClients = CLIENTS.filter(c => statuses[c.id] === 'sent')

  return (
    <div style={{ padding: 20, maxWidth: 1100 }}>

      {/* Header stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Messages queued', val: Object.keys(messages).length, color: '#C9973A' },
          { label: 'Approved', val: Object.values(statuses).filter(s=>s==='approved').length, color: '#3B6D11' },
          { label: 'Sent today', val: Object.values(statuses).filter(s=>s==='sent').length, color: '#185FA5' },
          { label: 'Urgent (< 10 days)', val: urgentCount, color: '#E24B4A' },
        ].map(s => (
          <div key={s.label} style={{ background: 'white', border: '0.5px solid #e0e3e9', borderRadius: 12, padding: '14px 16px', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 500 }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: 'white', border: '0.5px solid #e0e3e9', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[['queue','Message Queue'],['sent','Sent']].map(([key,label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ padding: '7px 16px', borderRadius: 7, border: 'none', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', background: tab===key ? '#0B1929' : 'transparent', color: tab===key ? '#C9973A' : '#666', transition: 'all .15s' }}>
            {label}{key==='queue' && Object.keys(messages).length > 0 ? ` (${Object.keys(messages).length})` : ''}
          </button>
        ))}
      </div>

      {tab === 'sent' ? (
        <div style={{ background: 'white', border: '0.5px solid #e0e3e9', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '11px 16px', background: '#f7f8fa', borderBottom: '0.5px solid #e0e3e9' }}>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Sent messages</span>
          </div>
          {sentClients.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#aaa' }}>
              <div style={{ fontSize: 28, marginBottom: 8, opacity: .3 }}>📤</div>
              <div style={{ fontSize: 13 }}>No messages sent yet</div>
            </div>
          ) : sentClients.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '0.5px solid #e0e3e9' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3B6D11', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{c.name}</div>
                <div style={{ fontSize: 11, color: '#888' }}>{c.filing} · {c.contact}</div>
              </div>
              <div style={{ fontSize: 11, color: '#3B6D11', fontWeight: 500 }}>✓ Sent</div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

          {/* Client list */}
          <div style={{ background: 'white', border: '0.5px solid #e0e3e9', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '11px 16px', background: '#f7f8fa', borderBottom: '0.5px solid #e0e3e9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Clients needing reminders</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {[['all','All'],['red','🔴'],['amber','🟡'],['green','🟢']].map(([key,label]) => (
                  <button key={key} onClick={() => setFilter(key)}
                    style={{ padding: '3px 9px', borderRadius: 6, border: '0.5px solid #ddd', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', background: filter===key ? '#0B1929' : 'white', color: filter===key ? '#C9973A' : '#666' }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {filtered.map((c, i) => {
              const urg = URGENCY_COLORS[c.urgency]
              const status = statuses[c.id]
              const isSelected = selected === c.id
              return (
                <div key={c.id}
                  style={{ padding: '12px 16px', borderBottom: i < filtered.length-1 ? '0.5px solid #e0e3e9' : 'none', background: isSelected ? '#f0f4ff' : 'white', cursor: 'pointer', transition: 'background .15s' }}
                  onClick={() => setSelected(isSelected ? null : c.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ width: 3, alignSelf: 'stretch', minHeight: 42, borderRadius: 2, background: urg.dot, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 1 }}>{c.name}</div>
                          <div style={{ fontSize: 11, color: '#888' }}>{c.filing}</div>
                          <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>Last contact: {c.lastContact}</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                          <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 99, background: urg.bg, color: urg.text, border: `0.5px solid ${urg.border}`, fontWeight: 600 }}>{c.daysLeft}d · {urg.label}</span>
                          {status && <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 99, background: status==='sent' ? '#EAF3DE' : status==='approved' ? '#E8F0FE' : '#FFF8E6', color: status==='sent' ? '#3B6D11' : status==='approved' ? '#185FA5' : '#854F0B', fontWeight: 500 }}>{status==='sent' ? '✓ Sent' : status==='approved' ? '✓ Approved' : '✎ Draft'}</span>}
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                        <button onClick={e => { e.stopPropagation(); generateMessage(c, false) }}
                          style={{ fontSize: 10, padding: '4px 10px', borderRadius: 6, border: '0.5px solid #C9973A', background: 'rgba(201,151,58,0.07)', color: '#7A4A10', cursor: 'pointer', fontFamily: 'inherit' }}>
                          ✎ Draft
                        </button>
                        <button onClick={e => { e.stopPropagation(); generateMessage(c, true) }}
                          disabled={aiLoading[c.id]}
                          style={{ fontSize: 10, padding: '4px 10px', borderRadius: 6, border: '0.5px solid #185FA5', background: 'rgba(24,95,165,0.07)', color: '#185FA5', cursor: 'pointer', fontFamily: 'inherit', opacity: aiLoading[c.id] ? .5 : 1 }}>
                          {aiLoading[c.id] ? '⏳ AI writing...' : '✨ AI draft'}
                        </button>
                        {status === 'approved' && (
                          <button onClick={e => { e.stopPropagation(); sendMessage(c) }}
                            style={{ fontSize: 10, padding: '4px 10px', borderRadius: 6, border: 'none', background: '#25D366', color: 'white', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                            ➤ Send on WhatsApp
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Message preview / editor */}
          <div style={{ background: 'white', border: '0.5px solid #e0e3e9', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {!selected ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 40, color: '#aaa', textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 12, opacity: .25 }}>💬</div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 6 }}>No message selected</div>
                <div style={{ fontSize: 12, lineHeight: 1.6 }}>Click "Draft" or "AI draft" next to a client to generate a WhatsApp message, then approve and send it.</div>
              </div>
            ) : (() => {
              const c = CLIENTS.find(x => x.id === selected)
              const msg = messages[selected] || ''
              const status = statuses[selected]
              const urg = URGENCY_COLORS[c.urgency]
              return (
                <>
                  <div style={{ padding: '11px 16px', background: '#f7f8fa', borderBottom: '0.5px solid #e0e3e9' }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{c.contact} · {c.phone} · {c.filing}</div>
                  </div>

                  {/* WhatsApp preview */}
                  <div style={{ flex: 1, padding: 14, background: '#ECE5DD', overflowY: 'auto' }}>
                    {msg ? (
                      <div style={{ maxWidth: '85%', marginLeft: 'auto' }}>
                        <div style={{ background: '#DCF8C6', borderRadius: '12px 12px 2px 12px', padding: '10px 13px', fontSize: 13, lineHeight: 1.65, whiteSpace: 'pre-wrap', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                          {msg}
                        </div>
                        <div style={{ fontSize: 10, color: '#aaa', textAlign: 'right', marginTop: 4 }}>
                          {status === 'sent' ? '✓✓ Delivered' : status === 'approved' ? '✓ Ready to send' : '✎ Draft'}
                        </div>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', paddingTop: 40, color: '#999', fontSize: 12 }}>
                        Click "Draft" or "AI draft" to generate a message
                      </div>
                    )}
                  </div>

                  {/* Edit area */}
                  {msg && (
                    <div style={{ padding: 12, borderTop: '0.5px solid #e0e3e9' }}>
                      <textarea
                        value={msg}
                        onChange={e => setMessages(prev => ({ ...prev, [selected]: e.target.value }))}
                        style={{ width: '100%', border: '0.5px solid #ccc', borderRadius: 8, padding: '9px 12px', fontSize: 12, fontFamily: 'inherit', resize: 'none', outline: 'none', lineHeight: 1.6, marginBottom: 8 }}
                        rows={5}
                      />
                      <div style={{ display: 'flex', gap: 8 }}>
                        {status !== 'approved' && status !== 'sent' && (
                          <button onClick={() => approve(selected)}
                            style={{ flex: 1, background: '#0B1929', color: '#C9973A', border: 'none', borderRadius: 8, padding: '9px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                            ✓ Approve message
                          </button>
                        )}
                        {(status === 'approved' || status === 'sent') && (
                          <button onClick={() => sendMessage(c)}
                            style={{ flex: 1, background: '#25D366', color: 'white', border: 'none', borderRadius: 8, padding: '9px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                            ➤ Open in WhatsApp & Send
                          </button>
                        )}
                        <button onClick={() => generateMessage(c, true)} disabled={aiLoading[c.id]}
                          style={{ padding: '9px 14px', background: 'rgba(24,95,165,0.08)', border: '0.5px solid #185FA5', color: '#185FA5', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                          {aiLoading[c.id] ? '⏳' : '✨ Regenerate'}
                        </button>
                      </div>
                      {status === 'sent' && (
                        <div style={{ marginTop: 8, background: '#EAF3DE', borderRadius: 7, padding: '7px 10px', fontSize: 11, color: '#3B6D11', textAlign: 'center' }}>
                          ✓ Message sent via WhatsApp · {c.contact} notified
                        </div>
                      )}
                    </div>
                  )}
                </>
              )
            })()}
          </div>
        </div>
      )}
    </div>
  )
}
